import { Alert } from '@mui/joy';
import { Warning as WarningIcon } from '@phosphor-icons/react';
import { mergeProps } from '@react-aria/utils';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { StatusCodes } from 'http-status-codes';
import React, { ComponentProps } from 'react';
import { z } from 'zod';

import { getCurrentIntl } from '~/i18n/intl/intl';

const intl = getCurrentIntl();

const arrayAPIError = z.object({
  message: z.array(z.string()),
});
type ArrayAPIError = z.infer<typeof arrayAPIError>;

type RESTAPIError = {
  status: string;
  error: string;
  message: string;
  details: string;
  response: {
    status: string;
    statusText: string;
    data: object | undefined;
  };
};

function isRESTAPIError(maybeError: unknown): maybeError is RESTAPIError {
  if (
    maybeError &&
    typeof maybeError === 'object' &&
    (('code' in maybeError &&
      'message' in maybeError &&
      'response' in maybeError) ||
      ('status' in maybeError && 'message' in maybeError))
  ) {
    return true;
  }
  return false;
}

function isRESTAPIErrorArray(maybeError: unknown): maybeError is ArrayAPIError {
  return arrayAPIError.safeParse(maybeError).success;
}

const stringArrayErrors = z.array(z.string());
function isErrorStringArray(
  maybeError: unknown,
): maybeError is z.infer<typeof stringArrayErrors> {
  return stringArrayErrors.safeParse(maybeError).success;
}

const nestedErrorKey = z.object({ error: z.string().min(1) });
function isNestedErrorValue(
  maybeError: unknown,
): maybeError is z.infer<typeof nestedErrorKey> {
  return nestedErrorKey.safeParse(maybeError).success;
}

const recordErrors = z.record(
  z.string(),
  z.union([z.string(), z.array(z.string())]),
);
function isErrorRecord(
  maybeError: unknown,
): maybeError is z.infer<typeof recordErrors> {
  return recordErrors.safeParse(maybeError).success;
}

export const standardErrorSchema = z.object({
  type: z.string(),
  errors: z.array(
    z.object({
      code: z.string(),
      detail: z.string(),
      attr: z.string().nullable(),
    }),
  ),
});

export function isStandardError(
  maybeError: unknown,
): maybeError is z.infer<typeof standardErrorSchema> {
  return standardErrorSchema.safeParse(maybeError).success;
}

/**
 * Type predicate to narrow an unknown error to `FetchBaseQueryError`
 */
export function isFetchBaseQueryError(
  error: unknown,
): error is FetchBaseQueryError {
  return typeof error === 'object' && error != null && 'status' in error;
}

export const getErrorMessage = (
  error: FetchBaseQueryError | SerializedError,
  defaultMessage = intl.formatMessage({
    // eslint-disable-next-line quotes
    defaultMessage: "Error encountered. We're currently fixing it.",
  }),
): string => {
  // New error handling
  if (isFetchBaseQueryError(error) && isStandardError(error.data)) {
    const errorMessages = error.data.errors.map(
      (serverError) => serverError.detail,
    );
    return errorMessages.join('\r\n');
  }

  // Deprecated error handling
  if ('status' in error && isRESTAPIError(error.data)) {
    return error.data.message;
  } else if ('status' in error && isErrorRecord(error.data)) {
    const message = Object.values(error.data).join(', ') || null;
    return message ?? defaultMessage;
  } else if ('status' in error && isRESTAPIErrorArray(error.data)) {
    const message = error.data.message.find((v) => v);
    return message ?? defaultMessage;
  } else if ('status' in error && isErrorStringArray(error.data)) {
    const message = error.data.find((v) => v);
    return message ?? defaultMessage;
  } else if ('status' in error && isNestedErrorValue(error.data)) {
    const message = error.data.error || null;
    return message ?? defaultMessage;
  }
  return defaultMessage;
};

/**
 * Type predicate to narrow an unknown error to an object with a string 'message' property
 */
export function isErrorWithMessage(error: unknown): error is {
  status: number;
  data: {
    error: string;
    message: string;
    status: number;
  };
} {
  return (
    isFetchBaseQueryError(error) &&
    'data' in error &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    typeof (error.data as any).message === 'string'
  );
}

/**
 * Type predicate to narrow an unknown error to an object with a string 'message' property
 */
export function isMutationError(error: unknown): error is {
  error: {
    status: number;
    data: {
      error: string;
      message: string;
      status: number;
    };
  };
} {
  return (
    typeof error === 'object' &&
    error != null &&
    'error' in error &&
    isErrorWithMessage(error.error)
  );
}

/**
 * Type predicate to narrow an unknown error to an object with a string 'message' property
 */
export function isHTTPRequestError(error: unknown): error is {
  status: number;
  data: {
    error: string;
    message: string;
    status: number;
  };
} {
  return (
    isFetchBaseQueryError(error) &&
    'data' in error &&
    'status' in error &&
    typeof error.status === 'number' &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    typeof (error.data as any).message === 'string'
  );
}

export function RequestErrorBanner({
  error,
  title,
}: Readonly<{
  title?: React.ReactNode;
  error?: FetchBaseQueryError | SerializedError;
}>) {
  if (!error) {
    return null;
  }

  const message = getErrorMessage(error);

  return (
    <Alert
      startDecorator={<WarningIcon weight='fill' />}
      variant='soft'
      color='danger'
    >
      <div className='flex flex-col'>
        {title ? <div className='text-sm/6 font-medium'>{title}</div> : null}
        <p className='text-sm/6 font-normal whitespace-pre-line'>{message}</p>
      </div>
    </Alert>
  );
}

type ErrorStatusCodes = Extract<
  StatusCodes,
  400 | 401 | 403 | 404 | 500 | 504 | 502
>;
const DEFAULT_ERROR_STATUS_MAP: Record<ErrorStatusCodes, string> = {
  400: intl.formatMessage({ defaultMessage: 'Bad Request' }),
  401: intl.formatMessage({ defaultMessage: 'Unauthorized' }),
  403: intl.formatMessage({ defaultMessage: 'Forbidden' }),
  404: intl.formatMessage({ defaultMessage: 'Not Found' }),
  500: intl.formatMessage({ defaultMessage: 'Internal Server Error' }),
  504: intl.formatMessage({ defaultMessage: 'Gateway Timeout' }),
  502: intl.formatMessage({ defaultMessage: 'Bad Gateway' }),
};
export function StatusCodeErrorBanner({
  error,
  statusCode,
  statusMap,
  size = 'md',
}: Readonly<{
  error?: FetchBaseQueryError | SerializedError;
  statusCode?: StatusCodes;
  statusMap?: Partial<Record<ErrorStatusCodes, string>>;
}> &
  Pick<ComponentProps<typeof Alert>, 'size'>) {
  if (!error) {
    return null;
  }

  const mergedMap = mergeProps(DEFAULT_ERROR_STATUS_MAP, statusMap ?? {});
  const code = isHTTPRequestError(error) ? error.status : statusCode;
  const isKnownCode = !!(code && code in mergedMap);

  if (!code || !isKnownCode) {
    return (
      <Alert
        size={size}
        startDecorator={<WarningIcon weight='fill' />}
        variant='soft'
        color='danger'
      >
        <p className='text-sm/6 font-normal whitespace-pre-line'>
          {getErrorMessage(error)}
        </p>
      </Alert>
    );
  }
  const message = mergedMap[code as ErrorStatusCodes];

  return (
    <Alert
      startDecorator={<WarningIcon weight='fill' />}
      variant='soft'
      color='danger'
    >
      {message}
    </Alert>
  );
}
